import {
  BaseLogo,
  GetProductMinQuantity,
  getLogoColors,
  isDigital,
} from "../../../../core/models/entities/BaseLogo";
import { DigitalColorElement } from "../../../components/Logo/DigitalColorElement";
import { PriceLine } from "../../../components/Logo/LogoPrices";
import { SalesPriceBadge } from "../../../components/Logo/SalesPriceBadge";
import { useSalesMarkup } from "../../../../hooks/useSalesMarkup";
import { roundSalesPrice } from "../../../../utils/functions/salesMarkup";
import { VectorColorElement } from "../../../components/Logo/VectorColorElement";
import {
  CartItemDesign,
  CartItemLogo,
  CartSummaryPrice,
  getPriceRowsCurrency,
} from "../models/CartItem";
import { useCartContext } from "../../../../contexts/CartContext";
import { LogoQuantitySelector } from "./LogoQuantitySelector";
import { NumberLogoStorage } from "../../../components/Logo/LogoStorage";
import { StorageUpdatedAt } from "../../../components/Logo/StorageUpdatedAt";
import { PriceTierNudge } from "../../../components/Logo/PriceTierNudge";
import { useProfileContext } from "../../../../contexts/ProfileContext";
import { useLogoContext } from "../../design-editor2/contexts/logo-options.context";
import { Color } from "../../../../core/models/entities/LogoOptions";
import { Dot } from "../../../components/Dot";
import { useOrderActionHandler } from "../hooks/useOrderActionsHandler";
import { useTranslation } from "react-i18next";
import { orderLineTitleText } from "../../order-details/subcomponents/OrderLineData";
import { isEmbroideryTransferType } from "../../../../utils/systemColors";
import { ProductPendingBadge } from "../../../components/Logo/ProductPendingBadge";
import { CartPriceUpdatedNote } from "../../../components/Logo/CartPriceUpdatedNote";
import {
  isLogoProductPending,
  isLogoUploadPending,
} from "../../../../utils/functions/productPending";
import { CLARITY_UNMASK, maskIfTextLogo } from "../../../../utils/analytics/clarityAttrs";

export function ProductDetailsLogo({
  orderLine,
  baseLogo,
}: {
  orderLine?: CartItemLogo;
  baseLogo?: BaseLogo;
}) {
  const { t } = useTranslation();
  const { cartSummary, items } = useCartContext();
  const { currency } = useProfileContext();
  // Sales price markup (flag priceMarkupEnabled): inert while off.
  const markup = useSalesMarkup();
  const logo = orderLine?.baseLogo ?? baseLogo;
  if (!logo) return null;

  const size = `${logo.widthMm}x${logo.heightMm}`;
  const type = logo.type;
  const method = logo.transferMethod.key;
  const storage = logo.inStorage;
  const prices =
    logo.product?.prices.sort((a, b) => a.minQuantity - b.minQuantity) ?? [];

  const isNumeric = logo.logoType === "NumberLogo";
  const isName = logo.logoType === "NameLogo";
  let colorsElements = getColorsElements(logo);

  // Aggregate this logo's quantities across the whole cart (standalone cart
  // line + every design that uses it) so the highlighted price tier reflects
  // the true total order, not just the row in front of the user. For
  // NumberLogo with a typed value, multiply by the digit count to match the
  // application-level pricing the confirmation page uses.
  const digitMultiplier = (v: string | number | undefined) =>
    isNumeric && v !== undefined ? `${v}`.length : 1;

  let aggregatedDelivery = 0;
  let aggregatedStorage = 0;
  for (const item of items) {
    if (item.type === "Logo") {
      const li = item as CartItemLogo;
      if (li.baseLogo.id !== logo.id) continue;
      const m = digitMultiplier(li.value) || 1;
      aggregatedDelivery += (li.toDelivery ?? 0) * m;
      aggregatedStorage += (li.toStorage ?? 0) * m;
    } else if (item.type === "Design") {
      const di = item as CartItemDesign;
      for (const inst of di.instances) {
        const instD = inst.toDelivery ?? 0;
        const instS = inst.toStorage ?? 0;
        if (instD + instS <= 0) continue;
        for (const il of inst.logos) {
          if (il.logoId !== logo.id) continue;
          const m = digitMultiplier(il.value) || 1;
          aggregatedDelivery += instD * m;
          aggregatedStorage += instS * m;
        }
      }
    }
  }

  // Negative inStorage (e.g., over-consumed historic storage) is treated as 0.
  const storageQty = Math.max(0, storage ?? 0);
  const payableQuantity =
    Math.max(0, aggregatedDelivery - storageQty) +
    Math.max(0, aggregatedStorage);

  // Only highlight if there's a payable quantity (storage doesn't cover everything)
  let highlightedMinQuantity: number | undefined =
    payableQuantity > 0
      ? GetProductMinQuantity(prices, payableQuantity)
      : undefined;

  const fees =
    cartSummary?.prices?.filter((p) => p.relatedTo == logo.product?.sku) ?? [];

  // Fee-line label: the fee amounts come out of the API's price rows (fees
  // are converted to the same target currency as the product's rows), so
  // label them with the ROWS' currency, not the profile's — same principle
  // as the cart header/draft (#603). Identical for DKK orgs; under act-as
  // the profile currency could mislabel foreign-priced rows.
  const feeCurrency = getPriceRowsCurrency(prices, currency);

  const { role } = useProfileContext();
  const isCustomer = role === 0;
  // Fee amount to print: the customer price for a customer login (as
  // today); with the sales price markup on, the sales price of the purchase
  // fee (a customer commission wins), rounded to the cent because this list
  // prints the raw number without toFixed.
  const feePriceToShow = (fee: CartSummaryPrice) =>
    markup.active
      ? roundSalesPrice(markup.price(fee.unitPrice, fee.customerPrice))
      : isCustomer && fee.customerPrice !== undefined && fee.customerPrice > 0
        ? fee.customerPrice
        : fee.unitPrice;

  // Nudge click: bump whichever bucket the user is already using. If both are
  // active, prefer Delivery. Skipped for Name/Number — they have a row-based
  // UI and (per product) almost always sit on a single price tier anyway.
  const { handleLogoSetQuantities } = useOrderActionHandler();
  const showNudge = !isNumeric && !isName;
  const handleNudgeAdd = orderLine
    ? (needed: number) => {
        const currentDelivery = orderLine.toDelivery ?? 0;
        const currentStorage = orderLine.toStorage ?? 0;
        if (currentDelivery > 0 || currentStorage === 0) {
          handleLogoSetQuantities(
            orderLine.baseLogo,
            currentDelivery + needed,
            currentStorage,
          );
        } else {
          handleLogoSetQuantities(
            orderLine.baseLogo,
            currentDelivery,
            currentStorage + needed,
          );
        }
      }
    : undefined;

  return (
    <div className="flex-1 flex flex-col gap-y-8">
      <div className="flex flex-row gap-x-2">
        <div className="flex-1 flex flex-col">
          <div className={"block text-sm font-bold uppercase"}>
            {t("orderCreate.details")}
          </div>
          {/* Clarity: SKU and size are non-personal (unmask, design 5.3);
              the name of a text/name/number logo is what the customer typed
              (mask, 5.2). The price block further down stays masked. */}
          <span className="block pt-1 text-xs">
            <p className={"font-bold capitalize inline pr-1"}>{t("item_no")}</p>
            <p className={"uppercase inline"} {...CLARITY_UNMASK}>
              {logo?.product?.sku}
            </p>
          </span>

          <span className="block pt-1 text-xs">
            <p className={"font-bold capitalize inline pr-1"}>{t("name")}</p>
            <p className={"inline"} {...maskIfTextLogo(logo?.logoType)}>
              {orderLine?.title ?? logo?.title}
            </p>
          </span>

          <span className="block pt-1 text-xs">
            <p className={"font-bold capitalize inline pr-1"}>
              {t("orderCreate.size")}
            </p>
            <p className={"inline"} {...CLARITY_UNMASK}>
              {size}mm
            </p>
          </span>

          <span className="block pt-1 text-xs">
            <p className={"font-bold capitalize inline pr-1"}>{t("type")}</p>
            <p className={"inline capitalize"}>
              {logo.transferMethod.type.toLowerCase() === "direk tryk" ||
              logo.transferMethod.type.toLowerCase() === "screen print"
                ? t("orderCreate.screenPrint")
                : logo.transferMethod.type.toLowerCase() === "broderi" ||
                    logo.transferMethod.type.toLowerCase() === "embroidery"
                  ? t("orderCreate.embroidery")
                  : logo.transferMethod.type
                      .toLowerCase()
                      .split(" ")
                      .map(
                        (word) => word.charAt(0).toUpperCase() + word.slice(1),
                      )
                      .join(" ")}
            </p>
          </span>

          {logo.transferMethod.type.toLowerCase() !== "direk tryk" &&
            logo.transferMethod.type.toLowerCase() !== "screen print" &&
            logo.transferMethod.type.toLowerCase() !== "broderi" &&
            logo.transferMethod.type.toLowerCase() !== "embroidery" && (
              <span className="block pt-1 text-xs">
                <p className={"font-bold capitalize inline pr-1"}>
                  {t("orderCreate.method")}
                </p>
                <p className={"inline capitalize"}>
                  {method.charAt(0).toUpperCase() +
                    method.slice(1).toLowerCase()}
                </p>
              </span>
            )}

          <span className="pt-1 text-xs flex flex-row">
            <p className={"font-bold capitalize inline pr-1"}>{t("colors")}</p>
            {colorsElements}
          </span>

          {!isNumeric && (
            <>
              <span className="flex gap-x-1 text-xs">
                <p className={"font-bold capitalize inline pr-1"}>
                  {t("orderCreate.inStorage")}
                </p>
                <span className={"flex items-center"} {...CLARITY_UNMASK}>
                  <Dot
                    className={`${
                      Math.max(0, storage ?? 0) > 0 ? "!bg-status-success" : ""
                    }`}
                  />
                  {Math.max(0, storage ?? 0)}
                </span>
              </span>
              <StorageUpdatedAt storageUpdatedAtUtc={logo.storageUpdatedAtUtc} />
            </>
          )}
        </div>

        <div className="flex-1 flex flex-col">
          <div className={"block text-sm font-bold uppercase"}>
            {t("orderCreate.prices")}
            {/* Sales price markup: the chip rides on the heading line, never
                as a row of its own above the tiers, and only while there are
                tiers to mark (not next to "Prices on their way"). Null with
                the flag off. */}
            {!isLogoProductPending(logo) && <SalesPriceBadge className="ml-1.5" />}
          </div>
          {/* "Preparing logo…" (flag cloneInstantEnabled): the clone still
              awaits its supplier identity - prices (the source's) stay,
              Continue is locked. Always false with the flag off. */}
          {isLogoUploadPending(logo) && (
            <div className="pt-1">
              <ProductPendingBadge
                variant="upload"
                pending={true}
                sinceUtc={logo.uploadPendingSinceUtc}
              />
            </div>
          )}
          {/* "Prices on their way" (flag productPendingEnabled): the badge
              replaces the (old or missing) price rows while the API worker
              still owes the product; cart sync patches the fresh product
              in. isLogoProductPending is always false with the flag off. */}
          {isLogoProductPending(logo) ? (
            <div className="pt-1">
              <ProductPendingBadge
                pending={true}
                sinceUtc={logo.productPendingSinceUtc}
              />
            </div>
          ) : (
            <>
              <div className={"grid grid-rows-6 grid-flow-col gap-0"}>
                {prices.map((p, i) => (
                  <span
                    key={i}
                    className={`pt-1 text-xs capitalize inline pr-1 ${
                      highlightedMinQuantity == p.minQuantity ? "font-bold" : ""
                    }`}
                  >
                    {/* A component, never PriceLine({...}): its hooks must
                        live in their own instance per tier, or this
                        component's hook count follows the number of
                        rows - React #311 when the price check changes it. */}
                    <PriceLine key={p.minQuantity} price={p} />
                  </span>
                ))}
              </div>
              {showNudge && (
                <PriceTierNudge
                  prices={logo.product?.prices}
                  currentTotal={payableQuantity}
                  onAddClick={handleNudgeAdd}
                />
              )}
              {/* "Price updated" (flag cartPriceCheckEnabled): the cart
                  price check landed a real change in this line's prices.
                  Renders nothing with the flag off. */}
              {orderLine && (
                <CartPriceUpdatedNote logoIds={[logo.id]} className="pt-1" />
              )}
            </>
          )}
        </div>

        {!isName && <div className="flex-1" />}
      </div>

      {isNumeric ? (
        <div className="flex flex-row gap-2 w-full">
          <div className="flex flex-row gap-2 flex-2">
            <LogoQuantitySelector logo={logo} orderLine={orderLine} />
          </div>
          <div className="flex-1 ml-4 flex flex-col gap-y-1">
            <div className={"block text-sm font-bold uppercase"}>
              {t("orderCreate.storageStatus")}
            </div>
            <NumberLogoStorage logo={logo} />
            <StorageUpdatedAt storageUpdatedAtUtc={logo.storageUpdatedAtUtc} />
          </div>
        </div>
      ) : (
        <div className="flex flex-row gap-2 w-full">
          <div className="flex flex-row gap-2 flex-1">
            <LogoQuantitySelector logo={logo} orderLine={orderLine} />
          </div>

          {fees.length > 0 &&
            fees.some((fee) => {
              const priceToUse = feePriceToShow(fee);
              return priceToUse > 0;
            }) && (
              <div className="flex-1">
                <div className={"block text-sm font-bold uppercase"}>
                  {t("orderCreate.extraFees")}
                </div>
                <ol>
                  {fees.map((fee, i) => {
                    // For startup costs, we need to calculate the proper display
                    // based on staircase pricing and number of colors

                    if (
                      fee.productName.toLowerCase().includes("startup") ||
                      fee.productName.toLowerCase().includes("start-up") ||
                      fee.productName.toLowerCase().includes("opstart")
                    ) {
                      // Get the number of colors in the logo
                      const numberOfColors = logo.colors?.length || 1;

                      const priceToUse = feePriceToShow(fee);

                      if (priceToUse === 0) return null;

                      return (
                        <li key={`${fee.productNo}_${i}`}>
                          {orderLineTitleText(fee.productName, t)} ({priceToUse}{" "}
                          {feeCurrency} x {numberOfColors})
                        </li>
                      );
                    } else {
                      // For regular fees, use the simple calculation
                      const quantity =
                        fee.unitPrice > 0
                          ? Math.round(fee.price / fee.unitPrice)
                          : 1;

                      const priceToUse = feePriceToShow(fee);

                      if (priceToUse === 0) return null;

                      return (
                        <li key={`${fee.productNo}_${i}`}>
                          {orderLineTitleText(fee.productName, t)} ({priceToUse}{" "}
                          {feeCurrency} x {quantity})
                        </li>
                      );
                    }
                  })}
                </ol>
              </div>
            )}

          {!isName && <div className="flex-1" />}
        </div>
      )}
    </div>
  );
}

interface ColorElementProps {
  logo: BaseLogo;
  colors: Color[];
}
export const ColorElement = ({ logo, colors }: ColorElementProps) => (
  <div className="flex flex-row">
    {isDigital(logo) ? (
      <DigitalColorElement showText={false} />
    ) : (
      <>
        {colors.map((color) =>
          color.isIndigo ? (
            <DigitalColorElement
              key={color.id}
              showText={false}
              text={color.name}
            />
          ) : (
            <VectorColorElement
              key={color.id}
              showName={false}
              color={color}
              embroideryMethod={isEmbroideryTransferType(
                logo.transferMethod?.type,
              )}
            />
          ),
        )}
      </>
    )}
  </div>
);

export function getColorsElements(logo: BaseLogo) {
  const { options } = useLogoContext();
  const colorOptions = options.colors;
  const colorsEmbroidery = options.colorsEmbroidery;
  const colors = getLogoColors(logo, colorOptions, colorsEmbroidery);
  return <ColorElement logo={logo} colors={colors} />;
}

export function getMethodDisplay(logo: BaseLogo) {
  const transferMethods = [
    logo.transferMethod.method1.toLocaleLowerCase() ?? "N/A",
    logo.transferMethod.method2.toLocaleLowerCase(),
    logo.transferMethod.method3.toLocaleLowerCase(),
  ].filter((x) => x?.length > 0);
  return <span className="capitalize">{transferMethods.join(", ")}</span>;
}
